import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import FloatingCallWidget from './FloatingCallWidget';
// Unused — replaced by unified WelcomeOnboarding component mounted at App.js
// import { OnboardingMiniPopup } from './OnboardingMiniPopup';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { Bell, X } from 'lucide-react';
import VendingOnboardingTour from '../VendingOnboardingTour';
import DeployHealthBanner from '../DeployHealthBanner';
import CurrencyGate from '../CurrencyGate';

function PushPromptBanner() {
  const { supported, permission, isSubscribed, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const d = localStorage.getItem('konnect_push_dismissed');
    if (d) setDismissed(true);
  }, []);

  if (!supported || isSubscribed || permission === 'denied' || dismissed) return null;

  const handleEnable = async () => {
    setLoading(true);
    const ok = await subscribe();
    setLoading(false);
    if (ok) setDismissed(true);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('konnect_push_dismissed', '1');
  };

  return (
    <div className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid="push-prompt-banner">
      <div className="flex items-center gap-2 min-w-0">
        <Bell className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">Get instant notifications for new inquiries and updates</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleEnable}
          disabled={loading}
          className="px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 text-xs font-medium transition-colors"
          data-testid="push-enable-btn"
        >
          {loading ? 'Enabling...' : 'Enable'}
        </button>
        <button onClick={handleDismiss} className="p-0.5 hover:bg-white/20 rounded" data-testid="push-dismiss-btn">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

import { DemoDataBanner } from '../DemoDataBanner';
// AutoMiniTour + FirstRealRecordModal + OnboardingMiniPopup intentionally removed.
// Standing order: ONE unified onboarding (WelcomeOnboarding + SampleDataTour at App.js).
import ChromeBrandProvider from '../ChromeBrandProvider';
import WhitelabelSetupBanner from '../WhitelabelSetupBanner';
import BrandSetupWizard from '../BrandSetupWizard';
import TourAudioConsentBanner from '../TourAudioConsentBanner';
import TrialBanner from '../TrialBanner';
import SyncNotifier from '../SyncNotifier';
import { UIBugReporterButton } from '../uibug/UIBugReporterButton';
import { useAuth } from '../../contexts/AuthContext';

export const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const onVendingRoute = location.pathname.startsWith('/vending');

  return (
    <div className="min-h-screen bg-off-white">
      <ChromeBrandProvider />
      <BrandSetupWizard />
      <SyncNotifier role={user?.role} />
      <UIBugReporterButton />
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content area — NO horizontal padding here. Each page wraps its
          own content with `page-shell-wide` / `page-shell-narrow` per the
          mobile-first standing order. Adding padding here would compound with
          the page's own padding and waste mobile screen width. */}
      <main className="pt-16 pb-20 md:pb-6 md:ml-64 min-h-screen">
        <WhitelabelSetupBanner />
        <TourAudioConsentBanner />
        <DeployHealthBanner />
        <DemoDataBanner />
        <PushPromptBanner />
        <TrialBanner />
        <Outlet />
      </main>

      <FloatingCallWidget />
      <BottomNav onNavigate={() => setSidebarOpen(false)} onOpenMenu={() => setSidebarOpen(true)} />
      {/* v2.5.897 — Vending tour is now available on every /vending/* page,
          not just the Fleet dashboard. Component is a no-op for users without
          vending access. Listens for the `vending-tour-replay` event dispatched
          by the header Replay button and the Help & Tours tab. */}
      {onVendingRoute && <VendingOnboardingTour />}
      <CurrencyGate />
    </div>
  );
};

export default AppLayout;
